import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Loader2, Send, Euro, Briefcase, FileCheck, Eye } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Applicant } from "@/types/applicant";
import EmailPreviewStep from "./EmailPreviewStep";
import { generateOfferPreviewHtml } from "@/lib/emailPreviewTemplates";

interface OfferEmailDialogProps {
  applicant: Applicant;
  onSuccess?: () => void;
}

const OfferEmailDialog = ({ applicant, onSuccess }: OfferEmailDialogProps) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(applicant.desired_position || "");
  const [salaryAmount, setSalaryAmount] = useState<number | "">("");
  const [salaryType, setSalaryType] = useState("monthly");
  const [startDate, setStartDate] = useState<Date>();
  const [weeklyHours, setWeeklyHours] = useState(40);
  const [vacationDays, setVacationDays] = useState(30);
  const [additionalBenefits, setAdditionalBenefits] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [companyName, setCompanyName] = useState("Unser Unternehmen");
  const [companyEmail, setCompanyEmail] = useState<string | undefined>();
  const [companyInfo, setCompanyInfo] = useState<{ logoUrl?: string; street?: string; houseNumber?: string; postalCode?: string; city?: string; phone?: string }>({});

  useEffect(() => {
    if (open) {
      supabase.from("company_settings").select("name, email, logo_url, street, house_number, postal_code, city, phone").limit(1).maybeSingle().then(({ data }) => {
        if (data?.name) setCompanyName(data.name);
        if (data?.email) setCompanyEmail(data.email);
        setCompanyInfo({
          logoUrl: data?.logo_url || undefined,
          street: data?.street || undefined,
          houseNumber: data?.house_number || undefined,
          postalCode: data?.postal_code || undefined,
          city: data?.city || undefined,
          phone: data?.phone || undefined,
        });
      });
    }
  }, [open]);

  const handleShowPreview = () => {
    if (!position || !salaryAmount) {
      toast.error("Bitte füllen Sie Position und Gehalt aus");
      return;
    }
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("send-offer-email", {
        body: {
          applicantId: applicant.id,
          position,
          salaryAmount: Number(salaryAmount),
          salaryType,
          startDate: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
          weeklyHours,
          vacationDays,
          additionalBenefits: additionalBenefits || undefined,
          customMessage: customMessage || undefined,
        },
      });
      if (error) throw error;
      toast.success("Angebot erfolgreich gesendet");
      setOpen(false);
      setShowPreview(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Error sending offer:", error);
      toast.error("Fehler beim Senden des Angebots");
    } finally {
      setLoading(false);
    }
  };

  const applicantName = `${applicant.first_name} ${applicant.last_name}`;
  const salaryTypeLabel = salaryType === "monthly" ? "monatlich" : "jährlich";
  const formattedSalary = salaryAmount ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(salaryAmount)) : "";
  const formattedStartDate = startDate
    ? startDate.toLocaleDateString("de-DE", { year: "numeric", month: "long", day: "numeric" })
    : "Nach Vereinbarung";

  const previewHtml = generateOfferPreviewHtml(
    applicantName, companyName, position, formattedSalary, salaryTypeLabel,
    weeklyHours, vacationDays, formattedStartDate,
    additionalBenefits || undefined, customMessage || undefined, companyEmail, companyInfo
  );
  const previewSubject = `Stellenangebot: ${position} bei ${companyName}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShowPreview(false); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          <FileCheck className="h-4 w-4 mr-2" />
          Angebot senden
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-green-600" />
            Stellenangebot senden
          </DialogTitle>
        </DialogHeader>

        {showPreview ? (
          <EmailPreviewStep
            html={previewHtml}
            subject={previewSubject}
            recipientEmail={applicant.email}
            onBack={() => setShowPreview(false)}
            onConfirmSend={handleSubmit}
            loading={loading}
            sendButtonLabel="Angebot senden"
            sendButtonClassName="bg-green-600 hover:bg-green-700"
            loadingIcon={<Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          />
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-900">
                <p className="text-sm"><strong>Bewerber:</strong> {applicantName}</p>
                <p className="text-sm text-muted-foreground">{applicant.email}</p>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Position *</Label>
                  <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="z.B. Softwareentwickler" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label className="flex items-center gap-2"><Euro className="h-4 w-4" />Gehalt *</Label>
                    <Input type="number" value={salaryAmount} onChange={(e) => setSalaryAmount(e.target.value ? Number(e.target.value) : "")} placeholder="z.B. 4500" />
                  </div>
                  <div className="grid gap-2">
                    <Label>Gehaltsart</Label>
                    <Select value={salaryType} onValueChange={setSalaryType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monatlich</SelectItem>
                        <SelectItem value="yearly">Jährlich</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Startdatum</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP", { locale: de }) : "Nach Vereinbarung"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={startDate} onSelect={setStartDate} locale={de} disabled={(d) => d < new Date()} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Wochenstunden</Label>
                    <Input type="number" value={weeklyHours} onChange={(e) => setWeeklyHours(Number(e.target.value))} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Urlaubstage/Jahr</Label>
                    <Input type="number" value={vacationDays} onChange={(e) => setVacationDays(Number(e.target.value))} />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Zusätzliche Leistungen (optional)</Label>
                  <Textarea placeholder="z.B. Firmenwagen, betriebliche Altersvorsorge..." value={additionalBenefits} onChange={(e) => setAdditionalBenefits(e.target.value)} rows={2} />
                </div>

                <div className="grid gap-2">
                  <Label>Persönliche Nachricht (optional)</Label>
                  <Textarea placeholder="z.B. Wir haben uns sehr gefreut, Sie kennenzulernen..." value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows={3} />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={handleShowPreview} variant="outline" disabled={!position || !salaryAmount}>
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OfferEmailDialog;
